import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

// MUI Components
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CardContent from '@mui/material/CardContent';
import Card from '@mui/material/Card';
import InputAdornment from '@mui/material/InputAdornment';
import CustomTextField from 'components/mui/text-field';
import CircularProgress from '@mui/material/CircularProgress';
import Backdrop from '@mui/material/Backdrop';
import LinearProgress from '@mui/material/LinearProgress';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

// Layout
import AuthIllustrationV1Wrapper from '../login/AuthIllustrationV1Wrapper';
import BlankLayout from '../../Layout/blankLayout';
import { CompleteInvitation, ValidateInviteToken } from './logic/InviteHandler';

const SetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [values, setValues] = useState({
    newPassword: '',
    showNewPassword: false,
    confirmNewPassword: '',
    showConfirmNewPassword: false,
  });

  const [isValid, setIsValid] = useState(null);
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Password validation functions
  const validatePassword = password => {
    const regex = /^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#^()_\-+=<>.,])[A-Za-z\d@$!%*?&#^()_\-+=<>.,]{8,}$/;
    return regex.test(password);
  };

  const checkPasswordGuidelines = (password) => {
    return {
      length: password.length >= 8,
      upperCase: /[A-Z]/.test(password),
      number: /\d/.test(password),
      specialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    }
  };

  const calculatePasswordStrength = (guidelines) => {
    const criteria = Object.values(guidelines).filter(value => value).length;
    return (criteria / 4) * 100;
  };

  const getColor = (passwordStrength) => {
    if (passwordStrength === 100) return '#1FC16B';
    if (passwordStrength >= 75) return '#FFA500';
    if (passwordStrength >= 50) return '#FFA500';
    return '#FF0000';
  };

  const passwordGuidelines = checkPasswordGuidelines(values.newPassword);
  const passwordStrength = calculatePasswordStrength(passwordGuidelines);

  useEffect(() => {
    const validateToken = async () => {
      try {
        const res = await ValidateInviteToken(token);
        if (res.isError) throw new Error(res.message);
        setEmail(res.result.email);
        setIsValid(!res.isError);
      } catch (err) {
        setErrors({ message: err.response?.message || 'Invalid or expired token.' });
        setIsValid(false);
      }
    };

    if (token) validateToken();
    else {
      setErrors({ message: 'Invalid invitation link.' });
      setIsValid(false);
    }
  }, [token]);

  const handleNewPasswordChange = prop => event => {
    setValues({ ...values, [prop]: event.target.value });
    setErrors({ ...errors, [prop]: '' });
  };

  const handleClickShowNewPassword = () => {
    setValues({ ...values, showNewPassword: !values.showNewPassword });
  };

  const handleConfirmNewPasswordChange = prop => event => {
    setValues({ ...values, [prop]: event.target.value });
  };

  const handleClickShowConfirmNewPassword = () => {
    setValues({ ...values, showConfirmNewPassword: !values.showConfirmNewPassword });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};

    if (!values.newPassword) {
      newErrors.password1 = 'Please enter a password';
    } else if (!validatePassword(values.newPassword)) {
      newErrors.password1 = 'Password does not meet criteria';
    }

    if (!values.confirmNewPassword) {
      newErrors.password2 = 'Please confirm your password';
    } else if (values.newPassword !== values.confirmNewPassword) {
      newErrors.password2 = 'Passwords do not match';
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setLoading(true);
    try {
        const response = await CompleteInvitation({ token, password : values.newPassword });

      if (!response.isError) {
        navigate('/login', { state: { message: 'Password set successfully! Please login to continue.' } });
      } else {
        setErrors({ message: response.message });
      }
    } catch (err) {
      setErrors({ message: err.response?.message || 'Failed to set password. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  if (isValid === null) {
    return (
      <BlankLayout>
        <Box className='content-center'>
          <AuthIllustrationV1Wrapper>
            <Card variant='outlined'>
              <CardContent sx={{ p: theme => `${theme.spacing(10.5, 8, 8)} !important`, textAlign: 'center' }}>
                <CircularProgress />
                <Typography variant='body1' sx={{ mt: 3 }}>Verifying your invitation...</Typography>
              </CardContent>
            </Card>
          </AuthIllustrationV1Wrapper>
        </Box>
      </BlankLayout>
    );
  }

  if (!isValid) {
    return (
      <BlankLayout>
        <Box className='content-center'>
          <AuthIllustrationV1Wrapper>
            <Card variant='outlined'>
              <CardContent sx={{ p: theme => `${theme.spacing(10.5, 8, 8)} !important`, textAlign: 'center' }}>
                <Typography variant='h5' color='error' gutterBottom>
                  Invalid Invitation
                </Typography>
                <Typography variant='body1' sx={{ mb: 2 }}>
                  {errors.message || 'The invitation link is invalid or has expired.'}
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  Please contact your administrator for a new invitation.
                </Typography>
              </CardContent>
            </Card>
          </AuthIllustrationV1Wrapper>
        </Box>
      </BlankLayout>
    );
  }

  return (
    <BlankLayout>
      <Box className='content-center'>
        <AuthIllustrationV1Wrapper>
          <Card variant='outlined'>
            <CardContent sx={{ p: theme => `${theme.spacing(10.5, 8, 8)} !important` }}>
              <Box sx={{ mb: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <img height='45' alt='logo' src='/images/Logo2.svg' />
              </Box>
              <Box sx={{ mb: 6, textAlign: 'center' }}>
                <Typography variant='h5' sx={{ mb: 1.5 }}>
                  Set Your Password
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  For: <strong>{email}</strong>
                </Typography>
              </Box>
              
              <form noValidate autoComplete='off' onSubmit={handleSubmit}>
                <CustomTextField
                  fullWidth
                  autoFocus
                  label='New Password'
                  placeholder='············'
                  value={values.newPassword}
                  sx={{ display: 'flex', mb: 4 }}
                  onChange={handleNewPasswordChange('newPassword')}
                  type={values.showNewPassword ? 'text' : 'password'}
                  error={!!errors.password1}
                  helperText={errors.password1}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position='end'>
                        <IconButton
                          edge='end'
                          onClick={handleClickShowNewPassword}
                          onMouseDown={e => e.preventDefault()}
                          aria-label='toggle password visibility'
                        >
                          {values.showNewPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
                
                <CustomTextField
                  fullWidth
                  label='Confirm Password'
                  placeholder='············'
                  sx={{ display: 'flex', mb: 1.5 }}
                  value={values.confirmNewPassword}
                  type={values.showConfirmNewPassword ? 'text' : 'password'}
                  onChange={handleConfirmNewPasswordChange('confirmNewPassword')}
                  error={!!errors.password2}
                  helperText={errors.password2}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position='end'>
                        <IconButton
                          edge='end'
                          onMouseDown={e => e.preventDefault()}
                          aria-label='toggle password visibility'
                          onClick={handleClickShowConfirmNewPassword}
                        >
                          {values.showConfirmNewPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
                
                <Box sx={{ my: 2 }}>
                  <LinearProgress 
                    value={passwordStrength} 
                    variant="determinate"
                    sx={{
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: getColor(passwordStrength)
                      },
                      height: '7px'
                    }}
                  />
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography sx={{ fontSize: '12px' }}>Must contain at least:</Typography>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.75 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', mb: 1 }}>
                      {passwordGuidelines.upperCase ? (
                        <CheckCircleIcon sx={{ color: '#1FC16B', height: '17px', width: '17px', mr: 1 }} />
                      ) : (
                        <CancelIcon sx={{ color: '#99A0AE', height: '17px', width: '17px', mr: 1 }} />
                      )}
                      <Typography sx={{ fontSize: '12px' }}>1 upper case</Typography>
                    </Box>
                    <Box sx={{ display: 'flex' }}>
                      {passwordGuidelines.number ? (
                        <CheckCircleIcon sx={{ color: '#1FC16B', height: '17px', width: '17px', mr: 1 }} />
                      ) : (
                        <CancelIcon sx={{ color: '#99A0AE', height: '17px', width: '17px', mr: 1 }} />
                      )}
                      <Typography sx={{ fontSize: '12px' }}> 1 number</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', mb: 1 }}>
                      {passwordGuidelines.specialChar ? (
                        <CheckCircleIcon sx={{ color: '#1FC16B', height: '17px', width: '17px', mr: 1 }} />
                      ) : (
                        <CancelIcon sx={{ color: '#99A0AE', height: '17px', width: '17px', mr: 1 }} />
                      )}
                      <Typography sx={{ fontSize: '12px' }}> 1 special character</Typography>
                    </Box>
                    <Box sx={{ display: 'flex' }}>
                      {passwordGuidelines.length ? (
                        <CheckCircleIcon sx={{ color: '#1FC16B', height: '17px', width: '17px', mr: 1 }} />
                      ) : (
                        <CancelIcon sx={{ color: '#99A0AE', height: '17px', width: '17px', mr: 1 }} />
                      )}
                      <Typography sx={{ fontSize: '12px' }}> 8 characters</Typography>
                    </Box>
                  </Box>
                </Box>
                
                {errors.message && (
                  <Typography color='error' sx={{ mb: 2 }}>
                    {errors.message}
                  </Typography>
                )}
                
                <Button 
                  fullWidth 
                  type='submit' 
                  variant='contained' 
                  color='main'
                  disabled={loading}
                >
                  {loading ? 'Setting Password...' : 'Set Password'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </AuthIllustrationV1Wrapper>
      </Box>
      
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </BlankLayout>
  );
};

export default SetPassword;